import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Schedule.module.scss';
import ScheduleStats from './components/ScheduleStats';
import ScheduleFilters from './components/ScheduleFilters';
import StudentScheduleTable from './components/StudentScheduleTable';
import ClinicalSiteScheduleTable from './components/ClinicalSiteScheduleTable';
import SpecialtyScheduleTable from './components/SpecialtyScheduleTable';
import Button from '../../../components/button/Button';
import Icon from '../../../components/icon/Icon';
import PageTitle from '../../../components/pageTitle/PageTitle';
import DropDownButton from '../../../components/dropDownButton/DropDownButton';
import ScheduledStudentsPopup from './components/ScheduledStudentsPopup';
import CustomSingleDatePicker from '../../../components/customSingleDatePicker/CustomSingleDatePicker';
import axiosClient from '../../../api/axiosClient';
import { useToasts } from '../../../contexts/ToastContext';
import { useProgram } from '../../../contexts/ProgramContext';
import { useAuth } from '../../../contexts/AuthContext';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';

import { eachDayOfInterval, format, isSameMonth } from 'date-fns';

const Schedule = () => {
  const navigate = useNavigate();
  const { showToast } = useToasts();
  const { programs, selectedProgramId, setSelectedProgramId } = useProgram();
  const { role, can } = useAuth();
  const canAutoSchedule = role !== 'Program Coordinator' || can('canAutoSchedule');
  const [activeTab, setActiveTab] = useState('student');
  const [dashboardStats, setDashboardStats] = useState(null);
  const [studentSchedules, setStudentSchedules] = useState([]);
  const [clinicalSiteSchedules, setClinicalSiteSchedules] = useState([]);
  const [specialtySchedules, setSpecialtySchedules] = useState([]);
  const [waitingList, setWaitingList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [isClinicalTableLoading, setIsClinicalTableLoading] = useState(false);
  const [isSpecialtyTableLoading, setIsSpecialtyTableLoading] = useState(false);

  const [activeFilters, setActiveFilters] = useState({
    student: [],
    clinicalSite: [],
    specialty: []
  });

  // Filter Master Data
  const [filterData, setFilterData] = useState({
    students: [],
    clinicalSites: [],
    specialties: [],
    instructors: []
  });

  // Date states
  const [scheduleDate, setScheduleDate] = useState(new Date());

  // Rotation states
  const [rotations, setRotations] = useState([]);
  const [selectedRotation, setSelectedRotation] = useState(null);
  const [programDates, setProgramDates] = useState({ startDate: null, endDate: null });

  const [studentsPopup, setStudentsPopup] = useState(null);

  const fetchDashboardStats = useCallback(async () => {
    if (!selectedProgramId || !scheduleDate) return;

    setIsLoading(true);
    try {
      const dateStr = format(scheduleDate, 'yyyy-MM-dd');
      const response = await axiosClient.get(`/program-schedules/${selectedProgramId}/dashboard`, {
        params: { date: dateStr }
      });

      const data = response.data.data || response.data;
      setDashboardStats(data);
      setRotations(data.rotations || []);
      setSelectedRotation(data.currentRotation || null);
      if (data.program) {
        setProgramDates({
          startDate: data.program.startDate ? new Date(data.program.startDate) : null,
          endDate: data.program.endDate ? new Date(data.program.endDate) : null
        });
      }
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [selectedProgramId, scheduleDate, showToast]);

  const fetchStudentSchedules = useCallback(async () => {
    if (!selectedProgramId || !selectedRotation || activeTab !== 'student') return;

    setIsTableLoading(true);
    try {
      const response = await axiosClient.get(`/program-schedules/${selectedProgramId}/calendar/students`, {
        params: {
          startDate: selectedRotation.startDate,
          endDate: selectedRotation.endDate,
          students: activeFilters.student,
          clinicalSites: activeFilters.clinicalSite,
          specialities: activeFilters.specialty
        }
      });

      setStudentSchedules(response.data.schedules || []);
      setWaitingList(response.data.waitlist || []);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsTableLoading(false);
    }
  }, [selectedProgramId, selectedRotation, activeTab, activeFilters, showToast]);

  const fetchClinicalSiteSchedules = useCallback(async () => {
    if (!selectedProgramId || !selectedRotation || activeTab !== 'clinical-site') return;

    setIsClinicalTableLoading(true);
    try {
      const response = await axiosClient.get(`/program-schedules/${selectedProgramId}/calendar/clinical-sites`, {
        params: {
          startDate: selectedRotation.startDate,
          endDate: selectedRotation.endDate,
          students: activeFilters.student,
          clinicalSites: activeFilters.clinicalSite,
          specialities: activeFilters.specialty
        }
      });

      setClinicalSiteSchedules(response.data.clinicalSites || []);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsClinicalTableLoading(false);
    }
  }, [selectedProgramId, selectedRotation, activeTab, activeFilters, showToast]);

  const fetchSpecialtySchedules = useCallback(async () => {
    if (!selectedProgramId || !selectedRotation || activeTab !== 'specialty') return;

    setIsSpecialtyTableLoading(true);
    try {
      const response = await axiosClient.get(`/program-schedules/${selectedProgramId}/calendar/specialities`, {
        params: {
          startDate: selectedRotation.startDate,
          endDate: selectedRotation.endDate,
          students: activeFilters.student,
          clinicalSites: activeFilters.clinicalSite,
          specialities: activeFilters.specialty
        }
      });

      setSpecialtySchedules(response.data.specialities || []);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsSpecialtyTableLoading(false);
    }
  }, [selectedProgramId, selectedRotation, activeTab, activeFilters, showToast]);

  const fetchFilterData = useCallback(async () => {
    if (!selectedProgramId) return;

    try {
      const [studentsRes, sitesRes, specsRes, instructorsRes] = await Promise.all([
        axiosClient.get(`/programs/${selectedProgramId}/students`),
        axiosClient.get(`/programs/${selectedProgramId}/clinical-sites`),
        axiosClient.get('specialities'),
        axiosClient.get('users/instructors')
      ]);

      setFilterData({
        students: studentsRes.data?.data || studentsRes.data?.students || [],
        clinicalSites: sitesRes.data?.data || sitesRes.data?.clinicalSites || (Array.isArray(sitesRes.data) ? sitesRes.data : []),
        specialties: specsRes.data || [],
        instructors: instructorsRes.data?.data || instructorsRes.data?.instructors || (Array.isArray(instructorsRes.data) ? instructorsRes.data : [])
      });
    } catch (error) {
      showToast(error.message, 'error');
    }
  }, [selectedProgramId, showToast]);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  useEffect(() => {
    fetchStudentSchedules();
  }, [fetchStudentSchedules]);

  useEffect(() => {
    fetchClinicalSiteSchedules();
  }, [fetchClinicalSiteSchedules]);

  useEffect(() => {
    fetchSpecialtySchedules();
  }, [fetchSpecialtySchedules]);

  useEffect(() => {
    fetchFilterData();
  }, [fetchFilterData]);

  useEffect(() => {
    if (!selectedRotation) {
      setStudentSchedules([]);
      setClinicalSiteSchedules([]);
      setSpecialtySchedules([]);
    }
  }, [selectedRotation]);

  const handlePrevRotation = () => {
    if (!selectedRotation) return;
    const currentIndex = rotations.findIndex(r => r.id === selectedRotation.id);
    if (currentIndex > 0) {
      setSelectedRotation(rotations[currentIndex - 1]);
    }
  };

  const handleNextRotation = () => {
    if (!selectedRotation) return;
    const currentIndex = rotations.findIndex(r => r.id === selectedRotation.id);
    if (currentIndex < rotations.length - 1) {
      setSelectedRotation(rotations[currentIndex + 1]);
    }
  };

  const getWeekRangeText = () => {
    if (!selectedRotation) return '';
    const start = new Date(selectedRotation.startDate);
    const end = new Date(selectedRotation.endDate);

    if (isSameMonth(start, end)) {
      return `${format(start, 'MMM d')}-${format(end, 'd, yyyy')}`;
    }
    return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
  };

  const weekDates = selectedRotation
    ? eachDayOfInterval({
        start: new Date(selectedRotation.startDate),
        end: new Date(selectedRotation.endDate)
      })
    : [];

  const handleProgramChange = useCallback((programId) => {
    if (programId === selectedProgramId) return;
    setSelectedProgramId(programId);
    setDashboardStats(null);
    setRotations([]);
    setSelectedRotation(null);
    setStudentSchedules([]);
    setClinicalSiteSchedules([]);
    setSpecialtySchedules([]);
  }, [selectedProgramId, setSelectedProgramId]);

  const programOptions = programs.map(p => ({
    label: p.title,
    onClick: () => handleProgramChange(p.id)
  }));

  const selectedProgramTitle = programs.find(p => p.id === selectedProgramId)?.title || 'Select Program';


  const handleExport = async () => {
    if (!selectedProgramId || !selectedRotation) return;

    const params = {
      startDate: selectedRotation.startDate,
      endDate: selectedRotation.endDate,
      students: activeFilters.student,
      clinicalSites: activeFilters.clinicalSite,
      specialities: activeFilters.specialty
    };

    const endpointMap = {
      'student': 'cohort-progress-report',
      'clinical-site': 'clinical-site-utilization-report',
      'specialty': 'speciality-completion-report'
    };
    const fileNameMap = {
      'student': 'cohort_progress_report.xlsx',
      'clinical-site': 'clinical_site_utilization_report.xlsx',
      'specialty': 'speciality_completion_report.xlsx'
    };

    setIsLoading(true);
    try {
      const response = await axiosClient.get(
        `/program-schedules/${selectedProgramId}/download/${endpointMap[activeTab]}`,
        {
          params,
          responseType: 'blob',
          transformResponse: [(data) => data]
        }
      );
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.download = fileNameMap[activeTab];
      link.click();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAutoSchedule = async () => {
    if (!selectedProgramId) return;

    setIsLoading(true);
    try {
      const response = await axiosClient.post(`/program-schedules/${selectedProgramId}/auto-generate-schedule`);
      showToast(response.data?.message || 'Schedule generated successfully', 'success');

      fetchStudentSchedules();
      fetchClinicalSiteSchedules();
      fetchSpecialtySchedules();
      fetchDashboardStats();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStudentsCellClick = (e, students, title, dateLabel) => {
    if (!students || students.length === 0) return;

    const cellRect = e.currentTarget.getBoundingClientRect();
    const POPUP_WIDTH = 320;
    const SPACING = 12;
    const viewportHeight = window.innerHeight;

    let leftPosition;
    const CHECK_WIDTH = 420;
    if (cellRect.right + SPACING + CHECK_WIDTH > window.innerWidth) {
      leftPosition = cellRect.left + window.scrollX - POPUP_WIDTH - SPACING;
    } else {
      leftPosition = cellRect.right + window.scrollX + SPACING;
    }

    const isNearBottom = cellRect.top + 350 > viewportHeight;

    setStudentsPopup({
      students,
      title,
      subtitle: dateLabel,
      position: {
        top: isNearBottom ? cellRect.top + window.scrollY : cellRect.top + window.scrollY,
        left: Math.max(0, leftPosition),
        isNearBottom
      }
    });
  };


  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <PageTitle label="Schedule" />
          <div className={styles.programDropdown}>
            <DropDownButton
              label={selectedProgramTitle}
              items={programOptions}
              type="button"
              size="small"
              variant="primary-light"
              position="bottom-left"
            />
          </div>
          <Icon
            name="settings"
            className={styles.settingsIcon}
            onClick={() => selectedProgramId && navigate(`/programs/edit/${selectedProgramId}`)}
          />
        </div>
        <div className={styles.actions}>
          <Button
            label={<span className={styles.editHistoryLabel}><Icon name="edit-history" />Edit History</span>}
            size="small"
            variant="primary-light"
            onClick={() => navigate('/schedule/history')}
          />
          <CustomSingleDatePicker
            date={scheduleDate}
            onChange={(date) => setScheduleDate(date)}
            minDate={programDates.startDate}
            maxDate={programDates.endDate}
          />
          {canAutoSchedule && (
            <Button
              label="Auto-Schedule"
              variant="primary"
              size='small'
              onClick={handleAutoSchedule}
              isLoading={isLoading}
            />
          )}
        </div>
      </div>

      <div className={styles.container}>
        <ScheduleStats stats={dashboardStats} isLoading={isLoading} />

        <div className={styles.content}>
          <ScheduleFilters
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            dateRangeText={getWeekRangeText()}
            onPrevWeek={handlePrevRotation}
            onNextWeek={handleNextRotation}
            filterCategories={[
              { id: 'student', label: 'Student', data: filterData.students, getLabel: (item) => `${item.firstName} ${item.lastName}`, type: 'user' },
              { id: 'clinicalSite', label: 'Clinical Site', data: filterData.clinicalSites, getLabel: (item) => item.name },
              { id: 'specialty', label: 'Specialty', data: filterData.specialties, getLabel: (item) => item.name },
            ]}
            filters={activeFilters}
            onFiltersChange={setActiveFilters}
            onExport={handleExport}
            hasRotation={!!selectedRotation}
          />

          <div className={styles.tableContainer}>
            {activeTab === 'student' && (
              <StudentScheduleTable
                data={studentSchedules}
                weekDates={weekDates}
                isLoading={isTableLoading}
                allClinicalSites={filterData.clinicalSites}
                allSpecialties={filterData.specialties}
                allInstructors={filterData.instructors}
                onRefresh={fetchStudentSchedules}
                programId={selectedProgramId}
                waitingList={waitingList}
              />
            )}
            {activeTab === 'clinical-site' && (
              <ClinicalSiteScheduleTable
                data={clinicalSiteSchedules}
                weekDates={weekDates}
                isLoading={isClinicalTableLoading}
                onCellClick={handleStudentsCellClick}
              />
            )}
            {activeTab === 'specialty' && (
              <SpecialtyScheduleTable
                data={specialtySchedules}
                weekDates={weekDates}
                isLoading={isSpecialtyTableLoading}
                onCellClick={handleStudentsCellClick}
              />
            )}
          </div>
        </div>
      </div>


      {studentsPopup && (
        <ScheduledStudentsPopup
          {...studentsPopup}
          onClose={() => setStudentsPopup(null)}
        />
      )}
    </>
  );
};

export default Schedule;
